import React, { useEffect, useState } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import {
  AppBar,
  Avatar,
  Box,
  Button,
  Chip,
  Container,
  Divider,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Add as AddIcon,
  Close as CloseIcon,
  Dashboard as DashboardIcon,
  GetApp as InstallIcon,
  Group as GroupIcon,
  HelpOutline as HelpIcon,
  Key as KeyIcon,
  ListAlt as ListAltIcon,
  Logout as LogoutIcon,
  Map as MapIcon,
  Menu as MenuIcon,
  Park as ParkIcon,
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import logo from '../../assets/logo-symbol.svg';
import ChangePasswordDialog from './ChangePasswordDialog';
import SyncStatus from './SyncStatus';
import { ConfirmDialog } from '../common';
import { useSync } from '../../context/SyncContext';
import { canInstall, promptInstall } from '../../serviceWorkerRegistration';
import IconAction from '../common/IconAction';
import { displayName, initialsOf } from '../../utils/people';

const NAV_ITEMS = [
  { label: 'Dashboard', icon: <DashboardIcon />, path: '/', match: (p) => p === '/' },
  { label: 'Map', icon: <MapIcon />, path: '/map', match: (p) => p.startsWith('/map') },
  {
    label: 'Surveys',
    icon: <ListAltIcon />,
    path: '/surveys',
    match: (p) => /^\/(surveys|transects|quadrats)/.test(p) && p !== '/surveys/new',
  },
  { label: 'Species', icon: <ParkIcon />, path: '/species', match: (p) => p.startsWith('/species') },
  {
    label: 'Users',
    icon: <GroupIcon />,
    path: '/admin/users',
    match: (p) => p.startsWith('/admin'),
    role: 'admin',
  },
];

// A nav entry: tinted when it's the current page, in the top bar and the drawer alike.
const navItemSx = (theme, active) => ({
  fontWeight: active ? 600 : 500,
  color: active ? 'primary.main' : 'text.secondary',
  bgcolor: active ? alpha(theme.palette.primary.main, 0.08) : 'transparent',
  '&:hover': { bgcolor: alpha(theme.palette.primary.main, active ? 0.12 : 0.06), color: 'primary.main' },
});

// The wordmark beside the logo, in the top bar and the drawer.
const WORDMARK_SX = { fontSize: '1.125rem', fontWeight: 700, color: 'primary.main', letterSpacing: '-0.01em' };

const Navbar = () => {
  const theme = useTheme();
  const location = useLocation();
  const { user, logout, hasPermission, hasRole } = useAuth();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  // Between the phone layout and wide screens (900-1199px) the full bar doesn't
  // fit: drop the link icons and the wordmark, keeping the logo mark.
  const compact = useMediaQuery(theme.breakpoints.down('lg')) && !isMobile;
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [changingPassword, setChangingPassword] = useState(false);
  const [confirmingSignOut, setConfirmingSignOut] = useState(false);
  const { pending } = useSync();

  // Unsynced changes stay on the device (per user) through sign-out, but say so first.
  const requestSignOut = () => (pending ? setConfirmingSignOut(true) : logout());
  const [installable, setInstallable] = useState(canInstall());

  useEffect(() => {
    const onInstallChange = () => setInstallable(canInstall());
    window.addEventListener('pwa:install', onInstallChange);
    return () => window.removeEventListener('pwa:install', onInstallChange);
  }, []);

  const canCreate = hasPermission('create_survey');
  const navItems = NAV_ITEMS.filter((item) => !item.role || hasRole(item.role));
  const isActive = (item) => item.match(location.pathname);

  // The account menu and the drawer offer the same actions, in the same order;
  // signing out comes last, under a divider.
  const accountItems = [
    installable && { key: 'install', label: 'Install app', icon: <InstallIcon fontSize="small" />, onClick: promptInstall },
    { key: 'help', label: 'User guide', icon: <HelpIcon fontSize="small" />, to: '/help' },
    { key: 'password', label: 'Change password', icon: <KeyIcon fontSize="small" />, onClick: () => setChangingPassword(true) },
    { key: 'signout', label: 'Sign out', icon: <LogoutIcon fontSize="small" />, onClick: requestSignOut, last: true },
  ].filter(Boolean);
  const linkProps = (item) => (item.to ? { component: RouterLink, to: item.to } : {});

  const userBlock = (
    <Box sx={{ px: 2, py: 1.5 }}>
      <Typography sx={{ fontWeight: 600 }} noWrap>
        {displayName(user)}
      </Typography>
      <Typography variant="body2" color="text.secondary" noWrap>
        {user.email}
      </Typography>
      <Stack direction="row" spacing={0.5} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
        {user.roles.map((role) => (
          <Chip key={role} label={role} variant="outlined" sx={{ textTransform: 'capitalize' }} />
        ))}
      </Stack>
    </Box>
  );

  return (
    <AppBar
      position="sticky"
      color="inherit"
      elevation={0}
      sx={{
        bgcolor: 'rgba(255, 255, 255, 0.92)',
        backdropFilter: 'blur(10px)',
        borderBottom: 1,
        borderColor: 'divider',
      }}
    >
      <Container maxWidth="xl">
        <Toolbar disableGutters sx={{ minHeight: { xs: 60, md: 68 }, gap: 1 }}>
          {isMobile && (
            <IconAction label="Open menu" edge="start" onClick={() => setDrawerOpen(true)} sx={{ mr: 0.5 }} icon={<MenuIcon />} />
          )}

          <Box
            component={RouterLink}
            to="/"
            aria-label="MangroveCollect home"
            sx={{ display: 'flex', alignItems: 'center', gap: 1.25, textDecoration: 'none', color: 'inherit', mr: isMobile ? 1 : compact ? 1.5 : 3, flexShrink: 0 }}
          >
            <Box component="img" src={logo} alt="" sx={{ height: 36, width: 'auto' }} />
            {!compact && (
              <Typography
                variant="h6"
                sx={{
                  ...WORDMARK_SX,
                  // Most phones (under 400px) have room for the mark only; the menu drawer shows the name.
                  '@media (max-width: 399.95px)': { display: 'none' },
                }}
              >
                MangroveCollect
              </Typography>
            )}
          </Box>

          {!isMobile && (
            <Stack direction="row" spacing={0.5} sx={{ flexGrow: 1, minWidth: 0 }}>
              {navItems.map((item) => {
                const active = isActive(item);
                return (
                  <Button
                    key={item.path}
                    component={RouterLink}
                    to={item.path}
                    startIcon={compact ? undefined : item.icon}
                    color="inherit"
                    sx={{ ...navItemSx(theme, active), px: compact ? 1.5 : 1.75, flexShrink: 0 }}
                  >
                    {item.label}
                  </Button>
                );
              })}
            </Stack>
          )}

          <Box sx={{ flexGrow: isMobile ? 1 : 0 }} />

          {canCreate && !isMobile && (
            <Button component={RouterLink} to="/surveys/new" variant="contained" startIcon={<AddIcon />} sx={{ mr: 1.5, flexShrink: 0 }}>
              New survey
            </Button>
          )}

          <SyncStatus />
          <IconAction
            label={displayName(user)}
            ariaLabel={`Account menu for ${displayName(user)}`}
            onClick={(e) => setMenuAnchor(e.currentTarget)}
            sx={{ p: 0.5 }}
            icon={<Avatar sx={{ bgcolor: 'primary.main' }}>{initialsOf(user)}</Avatar>}
          />
          <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
            {userBlock}
            <Divider sx={{ my: 0.5 }} />
            {accountItems.flatMap((item) => [
              item.last && <Divider key={`${item.key}-divider`} sx={{ my: 0.5 }} />,
              <MenuItem key={item.key} {...linkProps(item)} onClick={() => { setMenuAnchor(null); item.onClick?.(); }}>
                <ListItemIcon>{item.icon}</ListItemIcon>
                <ListItemText primary={item.label} />
              </MenuItem>,
            ].filter(Boolean))}
          </Menu>
        </Toolbar>
      </Container>

      <Drawer anchor="left" open={drawerOpen} onClose={() => setDrawerOpen(false)} PaperProps={{ sx: { width: 280, borderRadius: 0 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, minHeight: 60 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
            <Box component="img" src={logo} alt="" sx={{ height: 36, width: 'auto' }} />
            <Typography variant="h6" component="p" sx={WORDMARK_SX}>MangroveCollect</Typography>
          </Box>
          <IconAction label="Close menu" onClick={() => setDrawerOpen(false)} icon={<CloseIcon />} />
        </Box>
        <Divider />
        <List sx={{ px: 1.5, py: 1.5 }}>
          {navItems.map((item) => {
            const active = isActive(item);
            return (
              <ListItemButton
                key={item.path}
                component={RouterLink}
                to={item.path}
                onClick={() => setDrawerOpen(false)}
                aria-current={active ? 'page' : undefined}
                sx={{ ...navItemSx(theme, active), borderRadius: 2, mb: 0.5 }}
              >
                <ListItemIcon sx={{ color: 'inherit', '& svg': { fontSize: 20 } }}>{item.icon}</ListItemIcon>
                <ListItemText primary={item.label} primaryTypographyProps={{ fontWeight: 'inherit' }} />
              </ListItemButton>
            );
          })}
        </List>
        {canCreate && (
          <Box sx={{ px: 1.5 }}>
            <Button component={RouterLink} to="/surveys/new" onClick={() => setDrawerOpen(false)} variant="contained" fullWidth startIcon={<AddIcon />}>
              New survey
            </Button>
          </Box>
        )}
        <Box sx={{ mt: 'auto' }}>
          <Divider />
          {userBlock}
          <List sx={{ px: 1.5, pt: 0, pb: 1.5 }}>
            {accountItems.flatMap((item) => [
              item.last && <Divider key={`${item.key}-divider`} component="li" sx={{ my: 0.5 }} />,
              <ListItemButton
                key={item.key}
                {...linkProps(item)}
                onClick={() => { setDrawerOpen(false); item.onClick?.(); }}
                sx={{ ...navItemSx(theme, false), borderRadius: 2 }}
              >
                <ListItemIcon sx={{ color: 'inherit', '& svg': { fontSize: 20 } }}>{item.icon}</ListItemIcon>
                <ListItemText primary={item.label} primaryTypographyProps={{ fontWeight: 'inherit' }} />
              </ListItemButton>,
            ].filter(Boolean))}
          </List>
        </Box>
      </Drawer>

      <ChangePasswordDialog open={changingPassword} onClose={() => setChangingPassword(false)} />
      <ConfirmDialog
        open={confirmingSignOut}
        title="Sign out with changes not yet synced?"
        description={`${pending} change${pending === 1 ? '' : 's'} haven't reached the server yet. They stay on this device and will sync the next time you sign in here.`}
        confirmLabel="Sign out anyway"
        onConfirm={() => {
          setConfirmingSignOut(false);
          logout();
        }}
        onClose={() => setConfirmingSignOut(false)}
      />
    </AppBar>
  );
};

export default Navbar;
